import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface EnderecoCliente {
    id: number
    logradouro: string | null
    numero: string | null
    complemento: string | null
    referencia: string | null
    bairro: string | null
    cidade: string | null
    cep: string | null
    latitude: number | null
    longitude: number | null
}

export interface CartaoCliente {
    id: number
    nomeImpressoCartao: string | null
    numeroCartao: string | null
    bandeira: string | null
    codigoBanco: string | null
    cpf: string | null
}

export interface ClientePainel {
    id: number
    nome: string | null
    cpf: string | null
    email: string | null
    telefone: string | null
    /**ms ou null para limpar*/
    dataNascimento: number | null
    taxaEntrega: number | null
    observacao: string | null
    pontuarNaFidelidade: boolean
    receberSpam: boolean
    vip: boolean
    freteGratis: boolean
    ativo: boolean
    usuarioSimplificado: boolean
    permissoes: string[]
    enderecos: EnderecoCliente[]
    cartoes: CartaoCliente[]
}

export interface AtualizarClientePayload {
    nome: string
    cpf: string | null
    email: string
    telefone: string | null
    /** yyyy-MM-dd ou null para limpar */
    dataNascimento: string | null
    taxaEntrega: string | null
    observacao: string | null
    vip: boolean
    freteGratis: boolean
    pontuarNaFidelidade: boolean
    receberSpam: boolean
}

export interface ClienteAtualizado {
    id: number
    nome: string | null
    cpf: string | null
    email: string | null
    telefone: string | null
    dataNascimento: number | null
    taxaEntrega: number | null
    observacao: string | null
    pontuarNaFidelidade: boolean
    receberSpam: boolean
    vip: boolean
    freteGratis: boolean
}

export interface AtualizarEnderecoPayload {
    logradouro: string | null
    numero: string | null
    complemento: string | null
    referencia: string | null
    bairro: string | null
    cidade: string | null
    cep: string | null
}

export interface AgendarNotificacaoPayload {
    titulo: string
    mensagem: string
    /** yyyy-MM-dd */
    data: string
    /** HH:mm */
    hora: string
}

export interface RespostaMensagem {
    mensagem: string
}

export interface RespostaBloqueio {
    ativo: boolean
    mensagem: string
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

type RespostaErroRaw = { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

export const obterClientePainel = async function (
    authorization: string,
    idCliente: number
): Promise<ClientePainel | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<ClientePainel>>(`clientefiel/ObterClientePainel/${idCliente}`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: authorization,
                },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<ClientePainel>(response, "ObterClientePainel")
    } catch (error) {
        RegistrarLogErro(error, "ObterClientePainel", String(idCliente))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const atualizarClientePainel = async function (
    authorization: string,
    idCliente: number,
    payload: AtualizarClientePayload
): Promise<ClienteAtualizado | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<ClienteAtualizado>>(
                `clientefiel/AtualizarClientePainel/${idCliente}`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<ClienteAtualizado>(response, "AtualizarClientePainel")
    } catch (error) {
        RegistrarLogErro(error, "AtualizarClientePainel", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const redefinirSenhaClientePainel = async function (
    authorization: string,
    idCliente: number,
    novaSenha: string,
    confirmacaoNovaSenha: string
): Promise<RespostaMensagem | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<RespostaMensagem>>(
                `clientefiel/RedefinirSenhaClientePainel/${idCliente}`,
                { novaSenha, confirmacaoNovaSenha },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<RespostaMensagem>(response, "RedefinirSenhaClientePainel")
    } catch (error) {
        RegistrarLogErro(error, "RedefinirSenhaClientePainel", String(idCliente))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const atualizarEnderecoClientePainel = async function (
    authorization: string,
    idEndereco: number,
    payload: AtualizarEnderecoPayload
): Promise<EnderecoCliente | ErroApi> {
    try {
        const response = await api
            .put<RespostaRaw<EnderecoCliente>>(
                `clientefiel/AtualizarEnderecoClientePainel/${idEndereco}`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<EnderecoCliente>(response, "AtualizarEnderecoClientePainel")
    } catch (error) {
        RegistrarLogErro(error, "AtualizarEnderecoClientePainel", String(idEndereco))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const excluirEnderecoClientePainel = async function (
    authorization: string,
    idEndereco: number
): Promise<void | ErroApi> {
    try {
        const response = await api
            .delete<RespostaErroRaw | null>(
                `clientefiel/ExcluirEnderecoClientePainel/${idEndereco}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        const data = response.data
        if (data && data.codErro !== undefined && data.codErro > 0) {
            return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
        }
    } catch (error) {
        RegistrarLogErro(error, "ExcluirEnderecoClientePainel", String(idEndereco))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const bonificarSelosClientePainel = async function (
    authorization: string,
    idCliente: number,
    quantidade: number
): Promise<RespostaMensagem | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<RespostaMensagem>>(
                `clientefiel/BonificarSelosClientePainel/${idCliente}`,
                { quantidade },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<RespostaMensagem>(response, "BonificarSelosClientePainel")
    } catch (error) {
        RegistrarLogErro(error, "BonificarSelosClientePainel", String(idCliente))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const adicionarClienteCupomPainel = async function (
    authorization: string,
    idCliente: number,
    idCupom: number
): Promise<RespostaMensagem | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<RespostaMensagem>>(
                `clientefiel/AdicionarClienteCupomPainel/${idCliente}`,
                { idCupom },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<RespostaMensagem>(response, "AdicionarClienteCupomPainel")
    } catch (error) {
        RegistrarLogErro(error, "AdicionarClienteCupomPainel", String(idCliente))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const agendarNotificacaoClientePainel = async function (
    authorization: string,
    idCliente: number,
    payload: AgendarNotificacaoPayload
): Promise<RespostaMensagem | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<RespostaMensagem>>(
                `clientefiel/AgendarNotificacaoClientePainel/${idCliente}`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<RespostaMensagem>(response, "AgendarNotificacaoClientePainel")
    } catch (error) {
        RegistrarLogErro(error, "AgendarNotificacaoClientePainel", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const bloquearClientePainel = async function (
    authorization: string,
    idCliente: number,
    bloquear: boolean
): Promise<RespostaBloqueio | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<RespostaBloqueio>>(
                `clientefiel/BloquearClientePainel/${idCliente}`,
                { bloquear },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<RespostaBloqueio>(response, "BloquearClientePainel")
    } catch (error) {
        RegistrarLogErro(error, "BloquearClientePainel", String(idCliente))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const excluirClientePainel = async function (
    authorization: string,
    idCliente: number
): Promise<RespostaMensagem | ErroApi> {
    try {
        const response = await api
            .delete<RespostaRaw<RespostaMensagem>>(
                `clientefiel/ExcluirClientePainel/${idCliente}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<RespostaMensagem>(response, "ExcluirClientePainel")
    } catch (error) {
        RegistrarLogErro(error, "ExcluirClientePainel", String(idCliente))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
